import React, { useState, useEffect, useRef } from 'react';
import { useAudio } from '../contexts/AudioContext';

const GlobalAudioPlayer = () => {
  const { 
    currentTrack, 
    isPlaying, 
    duration, 
    currentTime, 
    volume,
    formatTime,
    togglePlay, 
    nextTrack, 
    previousTrack, 
    seekTo, 
    setVolume 
  } = useAudio();
  
  const [isMuted, setIsMuted] = useState(false);
  const [prevVolume, setPrevVolume] = useState(volume);
  const [isExpanded, setIsExpanded] = useState(false);
  const progressBarRef = useRef(null);
  const volumeBarRef = useRef(null);

  // Hide player if no track is selected
  if (!currentTrack) return null;

  // Calculate progress percentage
  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  // Handle progress bar click
  const handleProgressClick = (e) => {
    if (!progressBarRef.current) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickPosition = e.clientX - rect.left;
    const progressBarWidth = rect.width;
    const clickPercentage = clickPosition / progressBarWidth;
    const newTime = duration * clickPercentage;
    
    seekTo(newTime);
  };

  // Handle volume bar click
  const handleVolumeClick = (e) => {
    if (!volumeBarRef.current) return;
    
    const rect = volumeBarRef.current.getBoundingClientRect();
    const clickPosition = e.clientX - rect.left;
    const volumeBarWidth = rect.width;
    const newVolume = Math.max(0, Math.min(1, clickPosition / volumeBarWidth));
    
    setVolume(newVolume);
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (isMuted) {
      setVolume(prevVolume || 0.5);
      setIsMuted(false);
    } else {
      setPrevVolume(volume);
      setVolume(0);
      setIsMuted(true);
    }
  };

  // Toggle expanded view
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`fixed bottom-0 left-0 right-0 bg-gray-900 text-white shadow-lg transition-all duration-300 z-50 ${isExpanded ? 'h-24' : 'h-16'}`}>
      <div className="max-w-screen-xl mx-auto px-4 h-full flex items-center">
        {/* Track info */}
        <div className="flex items-center w-1/4">
          {currentTrack.thumbnailUrl && (
            <div className="w-10 h-10 bg-gray-800 rounded overflow-hidden mr-3 flex-shrink-0">
              <img 
                src={currentTrack.thumbnailUrl} 
                alt={currentTrack.title} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentNode.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                  `;
                }}
              />
            </div>
          )}
          <div className="truncate">
            <div className="font-medium truncate">{currentTrack.title}</div>
            <div className="text-xs text-gray-400 truncate">
              {currentTrack.albumTitle || 'No album'}
            </div>
          </div>
        </div>

        {/* Player controls */}
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="flex items-center space-x-4">
            {/* Previous button */}
            <button 
              onClick={previousTrack} 
              className="text-gray-400 hover:text-white transition-colors"
              aria-label="Previous track"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8.445 14.832A1 1 0 0010 14v-2.798l5.445 3.63A1 1 0 0017 14V6a1 1 0 00-1.555-.832L10 8.798V6a1 1 0 00-1.555-.832l-6 4a1 1 0 000 1.664l6 4z" />
              </svg>
            </button>
            
            {/* Play/Pause button */}
            <button 
              onClick={togglePlay} 
              className="bg-white rounded-full p-2 text-black hover:bg-gray-200 transition-colors"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
              )}
            </button>
            
            {/* Next button */}
            <button 
              onClick={nextTrack} 
              className="text-gray-400 hover:text-white transition-colors"
              aria-label="Next track"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555.832l6-4a1 1 0 000-1.664l-6-4A1 1 0 0010 6v2.798L4.555 5.168z" />
              </svg>
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="w-full flex items-center mt-2 text-xs space-x-2">
            <span className="text-gray-400 w-10 text-right">{formatTime(currentTime)}</span>
            <div 
              ref={progressBarRef}
              className="flex-1 h-1 bg-gray-700 rounded-full overflow-hidden cursor-pointer"
              onClick={handleProgressClick}
            >
              <div 
                className="h-full bg-green-500" 
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <span className="text-gray-400 w-10">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Volume and expand controls */}
        <div className="w-1/4 flex items-center justify-end space-x-4">
          {/* Volume control */}
          <div className="flex items-center space-x-2">
            <button 
              onClick={toggleMute} 
              className="text-gray-400 hover:text-white transition-colors"
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {volume === 0 || isMuted ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              ) : volume < 0.5 ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3.75a.75.75 0 00-1.264-.546L4.703 7H3.167a.75.75 0 00-.75.75v4.5c0 .414.336.75.75.75h1.536l4.033 3.796A.75.75 0 0010 16.25v-12.5zM11.5 10a2.25 2.25 0 011.088-1.93.75.75 0 01.824 1.26A.75.75 0 0013 10c0 .194.075.367.197.5a.75.75 0 01-1.038 1.084A2.25 2.25 0 0111.5 10z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071a1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243a1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828a1 1 0 010-1.415z" clipRule="evenodd" />
                </svg>
              )}
            </button>
            <div 
              ref={volumeBarRef}
              className="w-20 h-1 bg-gray-700 rounded-full overflow-hidden cursor-pointer hidden md:block"
              onClick={handleVolumeClick}
            >
              <div 
                className="h-full bg-green-500" 
                style={{ width: `${volume * 100}%` }}
              />
            </div>
          </div>
          
          {/* Expand/collapse button */}
          <button 
            onClick={toggleExpanded} 
            className="text-gray-400 hover:text-white transition-colors"
            aria-label={isExpanded ? 'Collapse player' : 'Expand player'}
          >
            {isExpanded ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlobalAudioPlayer;
